/**
 * DeadlineBadge Component
 *
 * Displays the deadline status with color coding based on urgency:
 * - Green: More than 7 days
 * - Yellow: 3-7 days
 * - Red: Less than 3 days or overdue
 * - Gray: Completed (approved)
 */

import { Timestamp } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import './DeadlineBadge.css';

interface DeadlineBadgeProps {
  dueDate: Timestamp;
  isCompleted?: boolean;
  showDays?: boolean;
  showLabel?: boolean;
  size?: 'small' | 'medium' | 'large';
}

export default function DeadlineBadge({
  dueDate,
  isCompleted = false,
  showDays = true,
  showLabel = false,
  size = 'medium',
}: DeadlineBadgeProps) {
  const { t, i18n } = useTranslation();
  const now = new Date();
  const dueDateObj = dueDate.toDate();
  const diffMs = dueDateObj.getTime() - now.getTime();
  const daysUntilDue = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  if (isCompleted) {
    return (
      <span className={`deadline-badge deadline-completed deadline-${size}`}>
        {showLabel && <span className="deadline-label">{t('deadline.due')}</span>}
        <span className="deadline-icon">✓</span>
        <span className="deadline-text">{formatDate(dueDateObj)}</span>
      </span>
    );
  }

  let urgencyClass = 'deadline-green';
  let icon = '🟢';

  if (daysUntilDue < 0) {
    urgencyClass = 'deadline-overdue';
    icon = '🔴';
  } else if (daysUntilDue <= 3) {
    urgencyClass = 'deadline-red';
    icon = '🔴';
  } else if (daysUntilDue <= 7) {
    urgencyClass = 'deadline-yellow';
    icon = '⚠️';
  }

  const daysText = daysUntilDue < 0
    ? t('deadline.overdue', { count: Math.abs(daysUntilDue) })
    : daysUntilDue === 0
    ? t('deadline.dueToday')
    : daysUntilDue === 1
    ? t('deadline.dueTomorrow')
    : `${daysUntilDue}d`;

  return (
    <span className={`deadline-badge ${urgencyClass} deadline-${size}`}>
      {showLabel && <span className="deadline-label">{t('deadline.due')}</span>}
      <span className="deadline-icon">{icon}</span>
      <span className="deadline-text">
        {formatDate(dueDateObj)}
        {showDays && <span className="deadline-days">({daysText})</span>}
      </span>
    </span>
  );
}

/**
 * Helper function to get deadline urgency level
 */
export function getDeadlineUrgency(dueDate: Timestamp): 'overdue' | 'urgent' | 'warning' | 'safe' | 'completed' {
  const now = new Date();
  const dueDateObj = dueDate.toDate();
  const diffMs = dueDateObj.getTime() - now.getTime();
  const daysUntilDue = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (daysUntilDue < 0) return 'overdue';
  if (daysUntilDue <= 3) return 'urgent';
  if (daysUntilDue <= 7) return 'warning';
  return 'safe';
}

/**
 * Helper function to calculate days until due
 */
export function getDaysUntilDue(dueDate: Timestamp): number {
  const now = new Date();
  const dueDateObj = dueDate.toDate();
  const diffMs = dueDateObj.getTime() - now.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}
